// ============================================================
// EACH SPECIAL'S LOOK (plan body-map H3; the bench's specials, as the user
// ruled them on 2026-10-09), built from the pieces (./primitives). A look
// gets the struck place and its part, when it starts, and a seed; it draws
// once. This file: the elements (what burns, sparks or eats in the wound)
// and the accents (holy, psychic and warp light). The context: ./kit.
// ============================================================

import type { ReactNode } from 'react';
import clsx from '../../../../utils/clsx';
import sx from '../../../../styles/PlateSpecials.module.css';
import {
  Bolts,
  Flash,
  jagged,
  Motes,
  radial,
  Rays,
  ringPoints,
  Smear,
  swingEnds,
  type Mote,
  type Point,
} from './primitives';
import { around, drawnIn, partGlow, swingOf, thrown, wholeLines, type LookCtx } from './kit';
import { timed } from '../timing';
import { tripletOf } from '../../../../engine/plate/triplet';

/** Melta: a white-hot point burns through; the lines round it glow orange and cool to red; molten drips. */
function melta(c: LookCtx) {
  const drips: Mote[] = [0, 1, 2, 3].map((i) => ({
    from: [c.at[0] + (i - 1.5) * 30 * c.k, c.at[1] + 40 * c.k],
    by: [0, 320 * c.k],
    delay: 0.3 + i * 0.15,
    r: 12 * c.k,
  }));
  return (
    <>
      <Flash at={c.at} r={80 * c.k} palette={sx.melta} start={c.start} dur={1.6} glow={c.glow} />
      {partGlow(c, { r: 260, palette: sx.meltaGlow, dur: 1.8 })}
      <Motes motes={drips} palette={sx.drip} start={c.start} dur={1} glow={c.glow} />
    </>
  );
}

/** Shock: a few fine arcs crawl near the wound. */
function shock(c: LookCtx) {
  const paths = [0, 1, 2, 3, 4, 5].map((i) =>
    jagged(c.at, around(c, 520, `s${i}`), 6, 50 * c.k, (s) => c.r(`s${i}${s}`)),
  );
  return <Bolts paths={paths} palette={sx.arc} width={4.5 * c.k} start={c.start} dur={0.7} glow={c.glow} />;
}

/** Shock on a blade: its lightning crawls along the edge of the swing, not over the body. */
function shockEdge(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const [A, B] = swingEnds(c.at, ang, len);
  const paths = [0, 1, 2].map((i) => jagged(A, B, 12, 70 * c.k, (s) => c.r(`e${i}${s}`)));
  return <Bolts paths={paths} palette={sx.arc} width={5 * c.k} start={c.start} dur={0.7} glow={c.glow} />;
}

/** Flame: the struck part catches - the On Fire condition's own burn, on that part alone, a moment. */
function flame(c: LookCtx) {
  const [x0, y0, x1, y1] = c.partBox;
  return partGlow(c, { r: Math.hypot(x1 - x0, y1 - y0), palette: '', className: sx.burn, dur: 0.9 });
}

/** Corrosive: the acid eats the part's lines round the wound, bubbling, and its armour falls. */
function corrosive(c: LookCtx) {
  const bubbles: Mote[] = Array.from({ length: 8 }, (_, i) => ({
    from: around(c, 220, `b${i}`),
    by: [0, -60 * c.k],
    delay: 0.2 + i * 0.12,
    r: (5 + 7 * c.r(`br${i}`)) * c.k,
  }));
  return (
    <>
      {partGlow(c, { r: 320, palette: sx.acid, dur: 2, width: 36 })}
      <Motes motes={bubbles} palette={sx.acidBubble} start={c.start} dur={0.7} />
      <text
        className={clsx(sx.word, sx.acid)}
        x={c.at[0] + 140 * c.k}
        y={c.at[1] - 60 * c.k}
        fontSize={110 * c.k}
        style={timed(c.start + 0.5)}
      >
        AP -1
      </text>
    </>
  );
}

/**
 * Tearing (ruled 2026-10-23): the hammer triplet - three cores in a tight triangle 70 ms apart, each white-hot to gold
 * to fire to ember with a shock ring and a fan of sparks; with motion off, three still red-hot marks (`data-still`).
 * The struck part's jolt is the hit's (fx/hits `Struck`).
 */
function tearing(c: LookCtx) {
  return tripletOf(c.at, c.k, c.r).map(({ at, delay, heat }, i) => {
    const start = c.start + delay;
    const [x, y] = at;
    return (
      <g key={i} data-core={i}>
        <Flash at={at} r={34 * heat * c.k} palette={sx.redHot} start={start} dur={0.9} glow={c.glow} />
        <circle className={sx.shockRing} cx={x} cy={y} r={30 * heat * c.k} strokeWidth={5 * c.k} style={timed(start)} />
        <Rays
          at={at}
          rays={radial(5, 40 * c.k, 60 * c.k, (s) => c.r(`t${i}${s}`), c.r(`tt${i}`) * Math.PI * 2)}
          palette={sx.spray}
          width={4 * c.k}
          start={start}
          dur={0.22}
        />
        <circle className={sx.stillMark} cx={x} cy={y} r={16 * c.k} data-still style={timed(c.start)} />
      </g>
    );
  });
}

/** The glass crater Felling leaves: a torn pane, cracks out from it, a glint. */
function crater(c: LookCtx) {
  const [x, y] = c.at;
  const pts = ringPoints(c.at, 9, (i) => (46 + 26 * c.r(`cr${i}`)) * c.k);
  const cracks = Array.from({ length: 6 }, (_, i) => {
    const a = c.r(`ca${i}`) * Math.PI * 2;
    const from: Point = [x + 50 * c.k * Math.cos(a), y + 50 * c.k * Math.sin(a)];
    return jagged(from, [x + 160 * c.k * Math.cos(a), y + 160 * c.k * Math.sin(a)], 4, 30 * c.k, (s) =>
      c.r(`cc${i}${s}`),
    );
  });
  return (
    <g className={sx.mark} style={timed(c.start + 0.15)}>
      <radialGradient id={`${c.id}glass`}>
        <stop offset={0} className={sx.glassDark} />
        <stop offset={0.55} className={sx.glassMid} />
        <stop offset={0.85} className={sx.glassCrack} />
        <stop offset={1} className={sx.glassEdge} />
      </radialGradient>
      <polygon points={pts} fill={`url(#${c.id}glass)`} className={sx.glassRim} strokeWidth={4 * c.k} />
      {cracks.map((d, i) => (
        <path key={i} d={d} className={sx.glassRim} fill="none" strokeWidth={3 * c.k} />
      ))}
    </g>
  );
}

/** Felling (user): a single sudden point of impact sheds a blinding light, and leaves a glass crater that fades. */
function felling(c: LookCtx) {
  return (
    <>
      <Flash at={c.at} r={300 * c.k} palette={sx.white} start={c.start} dur={0.5} glow={c.glow} />
      <Rays
        at={c.at}
        rays={radial(12, 260 * c.k, 160 * c.k, c.r)}
        palette={sx.white}
        width={7 * c.k}
        start={c.start}
        dur={0.3}
        glow={c.glow}
      />
      {partGlow(c, { r: 340, palette: sx.glassGlow, dur: 0.7 })}
      {crater(c)}
    </>
  );
}

/** Toxic: the poison bursts through the body from the wound (a Toughness test). */
function toxicw(c: LookCtx) {
  return partGlow(c, { r: 900, palette: sx.acid, dur: 3, width: 44 });
}

/** Volatile: a ten explodes - a beat after the hit, a second blast goes off in the wound. */
function volatile(c: LookCtx) {
  const t = c.start + 0.38;
  // (dir π: each piece's angle is its seed's turn, all the way round)
  const debris = thrown(c, { n: 16, dir: Math.PI, spread: Math.PI * 2, dist: 380, size: 8, salt: 'v' });
  return (
    <>
      <Flash at={c.at} r={220 * c.k} palette={sx.blast} start={t} dur={0.5} glow={c.glow} />
      <Motes motes={debris} palette={sx.debris} start={t} dur={0.8} />
    </>
  );
}

/** Sanctified: a narrow shaft of white-gold light strikes down onto the wound, the lines sear gold, sparks spray. */
function sanctified(c: LookCtx) {
  const top = c.figureBox[1] - 600 * c.k;
  const sparks = Array.from({ length: 12 }, (_, i) => ({
    a: Math.PI + c.r(`sa${i}`) * Math.PI,
    l: (160 + 140 * c.r(`sl${i}`)) * c.k,
  }));
  return (
    <>
      <Smear
        id={`${c.id}shaft`}
        at={[c.at[0], (top + c.at[1]) / 2]}
        ang={Math.PI / 2}
        len={c.at[1] - top}
        thick={40 * c.k}
        colour="var(--plate-sp-holy)"
        start={c.start}
        dur={0.6}
        glow={c.glow}
      />
      {partGlow(c, { r: 280, palette: sx.holy, dur: 2 })}
      <Rays at={c.at} rays={sparks} palette={sx.holySpark} width={5 * c.k} start={c.start + 0.15} dur={0.5} />
    </>
  );
}

/** Blessed: gentler - motes of warm gold drawn in onto the wound, and it glows warm a moment. */
function blessed(c: LookCtx) {
  return (
    <>
      <Motes
        motes={drawnIn(c, { n: 16, from: 320, spread: 0, salt: 'bl', stagger: 0.03 })}
        palette={sx.blessed}
        start={c.start}
        dur={1.1}
        glow={c.glow}
      />
      {partGlow(c, { r: 240, palette: sx.blessed, dur: 1.4, start: c.start + 0.9 })}
    </>
  );
}

/** Force: a psychic flare - rays out from the wound, the lines washed blue-white, lightning round it. */
function force(c: LookCtx) {
  const rays = Array.from({ length: 12 }, (_, i) => ({ a: (i / 12) * Math.PI * 2, l: (260 + 120 * (i % 3)) * c.k }));
  const bolts = [0, 1, 2].map((i) => {
    const a = c.r(`fa${i}`) * Math.PI * 2;
    const r = (200 + 90 * c.r(`fr${i}`)) * c.k;
    return jagged(c.at, [c.at[0] + r * Math.cos(a), c.at[1] + r * Math.sin(a)], 7, 50 * c.k, (s) => c.r(`fb${i}${s}`));
  });
  return (
    <>
      <Rays at={c.at} rays={rays} palette={sx.psyRay} width={6 * c.k} start={c.start} dur={0.7} glow={c.glow} />
      {partGlow(c, { r: 320, palette: sx.psy, dur: 1.2 })}
      <Bolts paths={bolts} palette={sx.psyRay} width={4 * c.k} start={c.start} dur={0.6} glow={c.glow} />
    </>
  );
}

/** Warp Weapon: a point of the warp opens on the wound - dark within, violet-rimmed - draws motes in, and seals. */
function warp(c: LookCtx) {
  return (
    <>
      <Flash at={c.at} r={80 * c.k} palette={sx.warpEye} start={c.start} dur={1.4} glow={c.glow} />
      <Motes
        motes={drawnIn(c, { n: 10, from: 260, spread: 120, salt: 'wp', stagger: 0.04 })}
        palette={sx.warp}
        start={c.start + 0.2}
        dur={0.9}
        glow={c.glow}
      />
    </>
  );
}

/** Exorcist: pale fire on the wound; on a daemon it runs over the whole body. */
function exorcist(c: LookCtx) {
  const flames = Array.from({ length: 12 }, (_, i) => {
    const [fx, fy] = around(c, 300, `ex${i}`);
    const h = (180 + 200 * c.r(`eh${i}`)) * c.k;
    const [w, n] = [60 * c.k, 26 * c.k];
    const d = `M${fx} ${fy} c${-w} ${-h * 0.3} ${-n} ${-h * 0.7} 0 ${-h} c${n} ${h * 0.3} ${w} ${h * 0.7} 0 ${h} z`;
    return { d, rise: -(100 + 80 * c.r(`er${i}`)) * c.k, delay: i * 0.06 };
  });
  return (
    <>
      <g className={sx.paleFire} filter={c.glow}>
        {flames.map((f, i) => (
          <path
            key={i}
            className={sx.mote}
            d={f.d}
            style={timed(c.start + f.delay, 1.3, { '--dx': 0, '--dy': Math.round(f.rise) })}
          />
        ))}
      </g>
      {c.daemonic && wholeLines(c, clsx(sx.glow, sx.paleFireGlow), timed(c.start, 1.8), { filter: c.glow })}
    </>
  );
}

/** Witchbane: a null - the colour drains from the part to ash grey and flecks of it fall. */
function witchbane(c: LookCtx) {
  const flecks: Mote[] = Array.from({ length: 14 }, (_, i) => ({
    from: around(c, 260, `wf${i}`),
    by: [0, 300 * c.k],
    delay: 0.2 + i * 0.05,
    r: 6 * c.k,
  }));
  return (
    <>
      {partGlow(c, { r: 360, palette: sx.null, dur: 2 })}
      <Motes motes={flecks} palette={sx.ash} start={c.start} dur={1.4} />
    </>
  );
}

/** The elements' and the accents' looks, by key. */
export const ELEMENT_LOOKS: Record<string, (c: LookCtx) => ReactNode> = {
  melta,
  shock,
  shockEdge,
  flame,
  corrosive,
  tearing,
  felling,
  toxicw,
  volatile,
  sanctified,
  blessed,
  force,
  warp,
  exorcist,
  witchbane,
};
